/**
 * Where "home" is for an authenticated user: the enabled feature home route
 * with the lowest `landingPriority` (Health Assistant → Diet → Dashboard →
 * the rest in sidebar order), additionally filtered by the paywall/BYOK layer
 * so an AI page is never chosen on a deployment where it cannot exist.
 */

import { useMemo } from "react";

import { usePaymentConfig, type PaymentConfigPublic } from "@shared/hooks/use-paywall";
import { matchAIFeatureKey } from "@shared/lib/paywall";
import {
  resolveLandingPath,
  type FeatureGatedRoute,
  type SiteFeatureFlags,
} from "@shared/lib/site-features";

import { useSiteFeatureFlags } from "./useSiteFeatureFlags";

type PaywallView = Pick<PaymentConfigPublic, "paymentsEnabled" | "byokEnabled"> & {
  aiFeaturesEnabled?: Record<string, boolean>;
};

/** Paywall/BYOK half of landing eligibility (flags are the other half). */
export function isLandingRouteEntitled(
  route: Pick<FeatureGatedRoute, "path">,
  config: PaywallView | null | undefined,
): boolean {
  const aiKey = matchAIFeatureKey(route.path);
  if (!aiKey) return true;
  if (!config) return false;
  const surfacesExist = Boolean(config.paymentsEnabled || config.byokEnabled);
  return surfacesExist && Boolean(config.aiFeaturesEnabled?.[aiKey]);
}

export function resolveEntitledLandingPath(
  flags: SiteFeatureFlags | null | undefined,
  config: PaywallView | null | undefined,
): string {
  return resolveLandingPath(flags, (route) => isLandingRouteEntitled(route, config));
}

export function useLandingPath(): string {
  const { data: flags } = useSiteFeatureFlags();
  const { data: paymentConfig } = usePaymentConfig();
  return useMemo(
    () => resolveEntitledLandingPath(flags, paymentConfig),
    [flags, paymentConfig],
  );
}
